import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import ProductSubdirectoryTableItem from './ProductSubdirectoryTableItem';
import {
  ICompanySubdirectorySubscriptionQueryData,
  ICompanySubdirectorySubscriptionVars,
  useCompanySubdirectorySubscriptionQuery,
} from '../../../../../graphql/query/companySubdirectorySubscription/useCompanySubdirectorySubscriptionQuery';
import GraphQLTable from '../../../../components/_table/GraphQLTable';
import Helmet from '../../../../components/Helmet';
import { ICompanySubdirectorySubscriptionSchema } from '../../../../../graphql/type/companySubdirectorySubscription';
import ProductLayout from '../../../../layout/Product';
import Context from '../../../../context';
import SelectYesNo from '../../../../components/_select/SelectYesNo';
import { useProductQuery } from '../../../../../graphql/query/product/useProductQuery';
import NotFound from '../../../../components/_error/NotFound';
import ProductDetailDropshippedProductInfo from '../_module/ProductDetailDropshippedProductInfo';
import useRouter from '../../../../components/_hook/useRouter';

function ProductSubdirectoryTable() {
  const { t } = useTranslation();
  const [tableRefreshKey, setTableRefreshKey] = useState<number>(0);
  const { company: contextCompany } = useContext(Context);
  const { params, history } = useRouter();
  const { docID: productId } = params;
  const productFragment = `
            _id
            isDropshippedProduct
            subdirectory {
              tags {
                status
                id
              }
            }
  `;
  const { data, loading } = useProductQuery(productFragment, {
    variables: {
      filter: {
        companyId: contextCompany._id,
        _id: productId as string,
      },
    },
    skip: productId === 'new',
  });

  if (productId !== 'new' && loading && productId) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress size={30} color="inherit" />
      </Grid>
    );
  }

  const product = productId && data ? data.products.items[0] : null;

  if (!product) {
    return <NotFound />;
  }

  if (product && product.isDropshippedProduct) {
    return <ProductDetailDropshippedProductInfo />;
  }

  return (
    <>
      <Helmet title={`${t('subdirectory', { count: 2 })} | ${t('product')}`} />
      <GraphQLTable<
        ICompanySubdirectorySubscriptionQueryData,
        ICompanySubdirectorySubscriptionSchema,
        ICompanySubdirectorySubscriptionVars
      >
        refreshKey={tableRefreshKey}
        select={`
				 _id
                company
                status
                expiryDate
                subdirectoryData {
                  _id
                  company
                  companyData {
                    _id
                    name {
                        la
                        text
                    }
                    origin
                    slug
                  }
                  status
                  domain
                  subdirectoryTagData {
                    _id
                    subdirectoryId
                    name
                  }
                }
       	 `}
        useQuery={useCompanySubdirectorySubscriptionQuery}
        renderHeader={[
          {
            field: '_id',
            label: t('id'),
            align: 'left',
            sortable: true,
          },
          {
            field: 'domain',
            label: `${t('subdirectory')} ${t('domain')}`,
            align: 'center',
          },
          {
            field: 'subscription_status',
            label: `${t('subscription')} ${t('status')}`,
            align: 'center',
          },
          {
            field: 'expiryDate',
            label: t('expiry_date'),
            align: 'center',
          },
          {
            field: 'product_subdirectory_status',
            label: `${t('product')} ${t('subdirectory')} ${t('tag')} ${t(
              'status'
            )}`,
            align: 'center',
          },
          {
            field: 'action',
            label: t('action'),
            align: 'right',
          },
        ]}
        renderItem={(item) => (
          <ProductSubdirectoryTableItem
            product={product}
            item={item}
            setTableRefreshKey={setTableRefreshKey}
          />
        )}
        filterFields={{
          status: {
            label: `${t('subscription')} ${t('status')}`,
            component: SelectYesNo,
            componentProps: {
              menuPosition: 'fixed',
              isClearable: true,
            },
            onChange: (option) => {
              if (option) {
                if (option.value === true) {
                  return true;
                }
                if (option.value === false) {
                  return false;
                }
              }

              return null;
            },
          },
        }}
        searchFields={{
          _id: { label: t('id') },
        }}
        defaultFilterOptions={{
          companyId: contextCompany._id,
        }}
        title={`${t('product')} ${t('subdirectory')}`}
        defaultSort="createdTime"
        defaultSortOrder={-1}
        itemKey="_id"
      />
    </>
  );
}

export default () => <ProductLayout main={<ProductSubdirectoryTable />} />;
